import { useEffect, useState, useRef } from "react";
import { Search, Package, AlertTriangle, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import api from "../services/api";
import Swal from "sweetalert2";

const MOTIVOS = [
  { value: "producto_vencido", label: "Producto vencido" },
  { value: "producto_dañado", label: "Producto dañado" },
  { value: "uso_interno", label: "Uso interno" },
  { value: "error_inventario", label: "Error de inventario" },
  { value: "otro", label: "Otro" },
];

export default function InventoryAdjustments() {
  // ── Buscador de productos ──
  const [searchProducto, setSearchProducto] = useState("");
  const [productos, setProductos] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);

  // ── Formulario de descarga ──
  const [cantidad, setCantidad] = useState("");
  const [motivo, setMotivo] = useState("");
  const [observacion, setObservacion] = useState("");
  const [guardando, setGuardando] = useState(false);

  // ── Historial ──
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filtroMotivo, setFiltroMotivo] = useState("");
  const [searchHistorial, setSearchHistorial] = useState("");

  const searchRef = useRef(null);

  // ── Buscar productos ──
  const buscarProductos = async (term) => {
    if (!term || term.length < 2) {
      setProductos([]);
      return;
    }
    setLoadingProductos(true);
    try {
      const res = await api.get(`/products/paginated?search=${term}&page=1`);
      setProductos(res.data.data || []);
    } catch (error) {
      console.error("Error buscando productos:", error);
    } finally {
      setLoadingProductos(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => buscarProductos(searchProducto), 400);
    return () => clearTimeout(timeout);
  }, [searchProducto]);

  // ── Seleccionar producto ──
  const seleccionarProducto = (producto) => {
    setProductoSeleccionado(producto);
    setSearchProducto(producto.nombre);
    setProductos([]);
    setCantidad("");
  };

  // ── Limpiar selección ──
  const limpiarFormulario = () => {
    setProductoSeleccionado(null);
    setSearchProducto("");
    setCantidad("");
    setMotivo("");
    setObservacion("");
    setProductos([]);
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  // ── Cargar historial ──
  const fetchHistorial = async (page = 1, mot = filtroMotivo, search = searchHistorial) => {
    setLoadingHistorial(true);
    try {
      const params = new URLSearchParams({ page });
      if (mot) params.append("motivo", mot);
      if (search) params.append("search", search);
      const res = await api.get(`/inventory-adjustments?${params}`);
      setHistorial(res.data.data || []);
      setCurrentPage(res.data.current_page);
      setTotalPages(res.data.last_page);
      setTotal(res.data.total);
    } catch (error) {
      console.error("Error cargando historial:", error);
    } finally {
      setLoadingHistorial(false);
    }
  };

  useEffect(() => {
    fetchHistorial();
  }, []);

  // ── Registrar descarga ──
  const handleSubmit = async () => {
    if (!productoSeleccionado) {
      Swal.fire("Atención", "Selecciona un producto", "warning");
      return;
    }
    if (!cantidad || parseInt(cantidad) <= 0) {
      Swal.fire("Atención", "Ingresa una cantidad válida", "warning");
      return;
    }
    if (!motivo) {
      Swal.fire("Atención", "Selecciona un motivo", "warning");
      return;
    }

    const confirmacion = await Swal.fire({
      title: "¿Confirmar descarga?",
      html: `
        <div style="text-align:left; padding: 8px 0;">
          <p><strong>Producto:</strong> ${productoSeleccionado.nombre}</p>
          <p><strong>Cantidad a descontar:</strong> ${cantidad}</p>
          <p><strong>Stock actual:</strong> ${productoSeleccionado.stock}</p>
          <p><strong>Stock después:</strong> ${productoSeleccionado.stock - parseInt(cantidad)}</p>
          <p><strong>Motivo:</strong> ${MOTIVOS.find(m => m.value === motivo)?.label}</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e53e3e",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, descontar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) return;

    setGuardando(true);
    try {
      await api.post("/inventory-adjustments", {
        product_id: productoSeleccionado.id,
        cantidad: parseInt(cantidad),
        motivo,
        observacion: observacion || null,
      });

      Swal.fire("✅ Registrado", "La descarga fue registrada correctamente", "success");
      limpiarFormulario();
      fetchHistorial(1);
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Error al registrar la descarga", "error");
    } finally {
      setGuardando(false);
    }
  };

  const getMotivoLabel = (value) => MOTIVOS.find(m => m.value === value)?.label || value;

  const getMotivoColor = (value) => {
    const colores = {
      producto_vencido: { bg: "#fff5f5", color: "#c53030", border: "#fc8181" },
      producto_dañado:  { bg: "#fffaf0", color: "#c05621", border: "#fbd38d" },
      uso_interno:      { bg: "#ebf8ff", color: "#2b6cb0", border: "#90cdf4" },
      error_inventario: { bg: "#f0fff4", color: "#276749", border: "#9ae6b4" },
      otro:             { bg: "#faf5ff", color: "#553c9a", border: "#d6bcfa" },
    };
    return colores[value] || { bg: "#f7fafc", color: "#4a5568", border: "#e2e8f0" };
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>

      {/* ── FORMULARIO DE DESCARGA ── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <AlertTriangle size={28} color="#e53e3e" />
            <h1 className="card-title">Descarga de Inventario</h1>
          </div>
        </div>

        <div className="card-body">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

            {/* Buscar producto */}
            <div className="form-group" style={{ position: "relative" }}>
              <label className="form-label">Producto *</label>
              <div style={{ position: "relative" }}>
                <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#a0aec0" }} />
                <input
                  ref={searchRef}
                  type="text"
                  className="form-control"
                  placeholder="Buscar producto por nombre o código..."
                  value={searchProducto}
                  onChange={(e) => {
                    setSearchProducto(e.target.value);
                    if (productoSeleccionado) setProductoSeleccionado(null);
                  }}
                  style={{ paddingLeft: 36 }}
                />
              </div>

              {/* Dropdown resultados */}
              {productos.length > 0 && !productoSeleccionado && (
                <div style={{ position: "absolute", zIndex: 100, width: "100%", background: "white", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", maxHeight: 220, overflowY: "auto" }}>
                  {loadingProductos ? (
                    <div style={{ padding: 16, textAlign: "center", color: "#718096" }}>Buscando...</div>
                  ) : (
                    productos.map((p) => (
                      <div
                        key={p.id}
                        onClick={() => seleccionarProducto(p)}
                        style={{ padding: "10px 16px", cursor: "pointer", borderBottom: "1px solid #f7fafc", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f7fafc"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                      >
                        <div>
                          <div style={{ fontWeight: 600, color: "#2d3748", fontSize: 14 }}>{p.nombre}</div>
                          <div style={{ fontSize: 12, color: "#718096" }}>Código: {p.codigo || "N/A"}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontWeight: 700, color: p.stock <= (p.stock_minimo || 5) ? "#ed8936" : "#48bb78", fontSize: 14 }}>
                            Stock: {p.stock}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Producto seleccionado */}
              {productoSeleccionado && (
                <div style={{ marginTop: 8, padding: "10px 14px", background: "#f0fff4", border: "2px solid #9ae6b4", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 600, color: "#276749" }}>{productoSeleccionado.nombre}</div>
                    <div style={{ fontSize: 12, color: "#48bb78" }}>Stock actual: <strong>{productoSeleccionado.stock}</strong> unidades</div>
                  </div>
                  <button onClick={limpiarFormulario} style={{ background: "none", border: "none", cursor: "pointer", color: "#718096" }}>
                    ✕
                  </button>
                </div>
              )}
            </div>

            {/* Cantidad */}
            <div className="form-group">
              <label className="form-label">Cantidad a descontar *</label>
              <input
                type="number"
                className="form-control"
                placeholder="Ej: 5"
                min="1"
                max={productoSeleccionado?.stock || 9999}
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
              />
              {productoSeleccionado && cantidad && parseInt(cantidad) > productoSeleccionado.stock && (
                <small style={{ color: "#e53e3e", fontSize: 12, marginTop: 4, display: "block" }}>
                  ⚠️ La cantidad supera el stock disponible ({productoSeleccionado.stock})
                </small>
              )}
              {productoSeleccionado && cantidad && parseInt(cantidad) <= productoSeleccionado.stock && (
                <small style={{ color: "#48bb78", fontSize: 12, marginTop: 4, display: "block" }}>
                  Stock después de descarga: <strong>{productoSeleccionado.stock - parseInt(cantidad)}</strong>
                </small>
              )}
            </div>

            {/* Motivo */}
            <div className="form-group">
              <label className="form-label">Motivo *</label>
              <select className="form-control" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
                <option value="">Seleccionar motivo...</option>
                {MOTIVOS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* Observación */}
            <div className="form-group">
              <label className="form-label">Observación <span style={{ color: "#a0aec0", fontWeight: 400 }}>(opcional)</span></label>
              <input
                type="text"
                className="form-control"
                placeholder="Detalle adicional..."
                value={observacion}
                onChange={(e) => setObservacion(e.target.value)}
                maxLength={500}
              />
            </div>
          </div>

          <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button className="btn btn-secondary" onClick={limpiarFormulario}>
              Limpiar
            </button>
            <button
              className="btn btn-danger"
              onClick={handleSubmit}
              disabled={guardando || !productoSeleccionado || !cantidad || !motivo}
            >
              {guardando ? "Registrando..." : "⬇️ Registrar Descarga"}
            </button>
          </div>
        </div>
      </div>

      {/* ── HISTORIAL ── */}
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <Package size={24} color="#667eea" />
            <h2 className="card-title" style={{ fontSize: 20 }}>Historial de Descargas</h2>
          </div>
          <span style={{ fontSize: 13, color: "#718096" }}>{total} registros</span>
        </div>

        <div className="card-body">
          {/* Filtros */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
              <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#a0aec0" }} />
              <input
                type="text"
                className="form-control"
                placeholder="Buscar en historial..."
                value={searchHistorial}
                onChange={(e) => {
                  setSearchHistorial(e.target.value);
                  clearTimeout(window.historialTimeout);
                  window.historialTimeout = setTimeout(() => fetchHistorial(1, filtroMotivo, e.target.value), 500);
                }}
                style={{ paddingLeft: 36 }}
              />
            </div>
            <select
              className="form-control"
              style={{ width: 200 }}
              value={filtroMotivo}
              onChange={(e) => {
                setFiltroMotivo(e.target.value);
                fetchHistorial(1, e.target.value, searchHistorial);
              }}
            >
              <option value="">Todos los motivos</option>
              {MOTIVOS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {loadingHistorial ? (
            <div className="text-center" style={{ padding: 40 }}>
              <div className="spinner" style={{ margin: "0 auto" }}></div>
              <p style={{ marginTop: 16, color: "#718096" }}>Cargando historial...</p>
            </div>
          ) : historial.length === 0 ? (
            <div className="text-center" style={{ padding: "40px 20px" }}>
              <AlertTriangle size={48} color="#cbd5e0" style={{ marginBottom: 16 }} />
              <p style={{ color: "#a0aec0", fontSize: 16, margin: 0 }}>No hay descargas registradas</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Producto</th>
                    <th style={{ textAlign: "center" }}>Cantidad</th>
                    <th style={{ textAlign: "center" }}>Stock Antes</th>
                    <th style={{ textAlign: "center" }}>Stock Después</th>
                    <th>Motivo</th>
                    <th>Observación</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map((h) => {
                    const color = getMotivoColor(h.motivo);
                    return (
                      <tr key={h.id}>
                        <td style={{ fontSize: 13, color: "#718096", whiteSpace: "nowrap" }}>
                          {new Date(h.created_at).toLocaleDateString('es-SV', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: "#2d3748" }}>{h.product?.nombre || "N/A"}</div>
                          <div style={{ fontSize: 12, color: "#718096" }}>{h.product?.codigo || ""}</div>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span style={{ fontWeight: 700, color: "#e53e3e", fontSize: 16 }}>-{h.cantidad}</span>
                        </td>
                        <td style={{ textAlign: "center", color: "#718096" }}>{h.stock_antes}</td>
                        <td style={{ textAlign: "center" }}>
                          <span style={{ fontWeight: 600, color: h.stock_despues <= 5 ? "#ed8936" : "#2d3748" }}>
                            {h.stock_despues <= 5 && <AlertTriangle size={14} style={{ marginRight: 4 }} />}
                            {h.stock_despues}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: 12, padding: "3px 10px", background: color.bg, color: color.color, border: `1px solid ${color.border}`, borderRadius: 6, fontWeight: 600, whiteSpace: "nowrap" }}>
                            {getMotivoLabel(h.motivo)}
                          </span>
                        </td>
                        <td style={{ fontSize: 13, color: "#718096", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {h.observacion || <span style={{ color: "#cbd5e0" }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div style={{ marginTop: 24, display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { fetchHistorial(currentPage - 1); }} disabled={currentPage === 1}>
                <ChevronLeft size={16} />
              </button>
              <span style={{ padding: "6px 12px", color: "#718096", fontSize: 14 }}>
                Página {currentPage} de {totalPages}
              </span>
              <button className="btn btn-secondary btn-sm" onClick={() => { fetchHistorial(currentPage + 1); }} disabled={currentPage === totalPages}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}